<template>
  <div class="body bank-app">
    <Navbar 
      :showBackButton="true" 
      @back="goBack"
    />
    
    <div class="main-content">
      
      <!-- Currency Prices Section -->
      <div v-if="hasPermission('ChartArz')" class="currency-prices-section" :class="{ 'expanded': showAllCurrencies }">
        <div class="currency-header">
          <h3 style="margin-right: 5px;">
            {{ $t('employer.dashboard.currencyPrices.title') }}
          </h3>

          <div class="price-time">{{ formatCurrencyTime() }}</div>

          <div class="header-actions">
            <div class="actions-group">
              <button class="toggle-btn" @click="currencyPriceCollapsed = !currencyPriceCollapsed"
                :title="currencyPriceCollapsed ? $t('employer.dashboard.showMore') : $t('employer.dashboard.showLess')">
                <span class="toggle-icon" :class="{ collapsed: currencyPriceCollapsed }">^</span>
                <span class="toggle-text">{{ currencyPriceCollapsed ? $t('employer.dashboard.showMore') : $t('employer.dashboard.showLess') }}</span>
              </button>
              
              <button class="refresh-btn" @click="refreshCurrencyPrices" :disabled="currencyLoading || refreshingCurrencies">
                <span class="refresh-icon">↻</span>
              </button>
            </div>

            <button 
              v-if="currencyPrices.length > 6"
              class="expand-btn"
              @click="showAllCurrencies = !showAllCurrencies"
              :class="{ 'expanded': showAllCurrencies }"
            >
              <span class="expand-icon">
                {{ showAllCurrencies ? '−' : '+' }}
              </span>
            </button>
          </div>
        </div>
        
        <!-- Loading State -->
        <div v-if="currencyLoading && !currencyPrices.length" class="currency-loading">
          <div class="loading-spinner"></div>
          <span>{{ $t('employer.dashboard.currencyPrices.loading') }}</span>
        </div>
        
        <!-- Error State -->
        <div v-else-if="currencyError && !currencyPrices.length" class="currency-error">
          <span class="error-icon">⚠️</span>
          <span>{{ currencyError }}</span>
          <button class="retry-btn" @click="fetchCurrenciesPrice">
            {{ $t('employer.dashboard.currencyPrices.retry') }}
          </button>
        </div>
        
        <!-- Data Display -->
        <div v-else-if="!currencyPriceCollapsed && currencyPrices.length > 0" class="currency-content">
          <div class="currency-grid-container">
            <div class="currency-grid">

              <div 
                v-for="currency in displayedCurrencies" 
                :key="currency['کد ارز']"
                class="currency-card"
                :class="{
                  'highlighted': isPopularCurrency(currency['کد ارز']),
                }"
              >
                <div class="currency-flag">
                  {{ getCurrencyFlag(currency['کد ارز']) }}
                </div>
                <div class="currency-flag">
                  <div class="currency-name">{{ currency['نام ارز'] }}</div>
                </div>
                <div class="currency-info">
                  <div class="currency-code">{{ currency['کد ارز'] }}</div>
                </div>
                <div class="currency-price">
                  <div class="price-value">{{ formatCurrencyPrice(currency['قیمت لحظه‌ای']) }}</div>
                </div>
              </div>

            </div>
          </div>
        </div>
        
        <!-- Empty State -->
        <div v-else-if="!currencyLoading && !currencyPriceCollapsed" class="currency-empty">
          <span class="empty-icon">💱</span>
          <span>{{ $t('employer.dashboard.currencyPrices.empty') }}</span>
        </div>
      </div>

      <!-- Daily Sale Report (NEW) - below currency prices -->
      <div v-if="hasPermission('ChartDailySale')" class="daily-sale-section">
        <div class="currency-header">
          <h3 style="margin-right: 5px; color: white;">
            {{ $t('employer.dashboard.dailySale.title') }}
          </h3>

          <div class="actions-group">
            <button class="toggle-btn"
                    @click="dailySaleCollapsed = !dailySaleCollapsed"
                    :title="dailySaleCollapsed ? $t('employer.dashboard.showMore') : $t('employer.dashboard.showLess')">
              <span class="toggle-icon" :class="{ collapsed: dailySaleCollapsed }">^</span>
              <span class="toggle-text">{{ dailySaleCollapsed ? $t('employer.dashboard.showMore') : $t('employer.dashboard.showLess') }}</span>
            </button>

              <button class="refresh-btn" @click="fetchDailySaleReport" :disabled="dailySaleLoading">
                <span class="refresh-icon">↻</span>
              </button>
          </div>
        </div>
        <DatePicker
          v-model="filters.dateTo"
          format="jYYYY/jMM/jDD"
          display-format="jYYYY/jMM/jDD"
          :placeholder="$t('employer.dashboard.selectDate')"
          :editable="true"
          input-class="persian-date-picker"
          :auto-submit="true"
        />

        <div v-if="dailySaleLoading" class="currency-loading">
          <div class="loading-spinner"></div>
        </div>

        <div v-else-if="dailySaleError" class="currency-error">
          <span>{{ dailySaleError }}</span>
        </div>

        <div v-else-if="!dailySaleCollapsed && dailySaleData.length > 0" class="daily-sale-content">
          <!-- Chart 1: Tedad -->
          <div class="chart-box">
            <div class="chart-header">
              <div class="chart-title">{{ $t('employer.dashboard.dailySale.chartCount') }}</div>

              <select class="chart-type-select" v-model="dailySaleChartTypeV">
                <option value="bar">{{ $t('employer.dashboard.dailySale.bar') }}</option>
                <option value="line">{{ $t('employer.dashboard.dailySale.line') }}</option>
                <option value="pie">{{ $t('employer.dashboard.dailySale.pie') }}</option>
              </select>
            </div>

            <div class="chart-container">
              <component
                :is="chartComponentMap[dailySaleChartTypeV]"
                :chartData="dailySaleChartDataV"
              />
            </div>
          </div>

          <!-- Chart 2: Mablagh -->
          <div class="chart-box">
            <div class="chart-header">
              <div class="chart-title">{{ $t('employer.dashboard.dailySale.chartAmount') }}</div>

              <select class="chart-type-select" v-model="dailySaleChartTypeM">
                <option value="bar">{{ $t('employer.dashboard.dailySale.bar') }}</option>
                <option value="line">{{ $t('employer.dashboard.dailySale.line') }}</option>
                <option value="pie">{{ $t('employer.dashboard.dailySale.pie') }}</option>
              </select>
            </div>

            <div class="chart-container">
              <component
                :is="chartComponentMap[dailySaleChartTypeM]"
                :chartData="dailySaleChartDataM"
              />
            </div>
          </div>
        </div>

        <div v-else-if="!dailySaleCollapsed" class="currency-empty">
          <span class="empty-icon">📈</span>
          <span>{{ $t('employer.dashboard.dailySale.empty') }}</span>
        </div>
      </div>

      <!-- Daily Activity Report Section -->
      <div v-if="hasPermission('ChartDailyCommercial')" class="daily-report-section">
        <div class="currency-header">
          <h3 style="margin-right: 5px; color: white;">
            {{ $t('employer.dashboard.dailyReport.title') }}
          </h3>
          <div class="actions-group">
            <button class="toggle-btn" @click="dailyReportCollapsed = !dailyReportCollapsed"
              :title="dailyReportCollapsed ? $t('employer.dashboard.showMore') : $t('employer.dashboard.showLess')">
              <span class="toggle-icon" :class="{ collapsed: dailyReportCollapsed }">^</span>
              <span class="toggle-text">{{ dailyReportCollapsed ? $t('employer.dashboard.showMore') : $t('employer.dashboard.showLess') }}</span>
            </button>
            
              
            <button class="refresh-btn" @click="fetchDailyReport" :disabled="reportLoading">
              <span class="refresh-icon">↻</span>
            </button>
          </div>
        </div>
            <DatePicker
              v-model="filters.dateTo"
              format="jYYYY/jMM/jDD"
              display-format="jYYYY/jMM/jDD"
              :placeholder="$t('employer.dashboard.selectDate')"
              :editable="true"
              input-class="persian-date-picker"
              :auto-submit="true"
              color="var(--primary)" 
            />
        <div v-if="reportLoading" class="currency-loading">
          <div class="loading-spinner"></div>
        </div>

        <div v-else-if="reportError" class="currency-error">
          <span>{{ reportError }}</span>
        </div>

        <div v-else-if="!dailyReportCollapsed && dailyReportData.length > 0" class="report-table-container">
          <table class="report-table">
            <thead>
              <tr>
                <th>{{ $t('employer.dashboard.dailyReport.title') }}</th>
                <th>{{ $t('employer.dashboard.dailyReport.count') }}</th>
                <th>{{ $t('employer.dashboard.dailyReport.amount') }}</th>
                <th v-if="showSumValueColumn">{{ $t('employer.dashboard.dailyReport.value') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in dailyReportData" :key="item.Act">
                <td class="title-cell amount-cell" :data-label="item.Act_Title">
                  <span class="desktop-text">{{ item.Act_Title }}</span>
                </td>
                
                <td class="amount-cell" :data-label="$t('employer.dashboard.dailyReport.count')">
                  <span class="price-item">{{ formatCurrencyPrice(item.Act_Ted) }}</span>
                </td>

                <td v-if="showSumValueColumn" class="amount-cell" :data-label="$t('employer.dashboard.dailyReport.value')">
                  <span class="price-item">{{ toNumber(item.Sum_Value) === 0 ? '-' : formatCurrencyPrice(item.Sum_Value) }}</span>
                </td>
                
                <td class="amount-cell" :data-label="$t('employer.dashboard.dailyReport.amount')">
                  <span class="price-item">{{ formatCurrencyPrice(item.Act_Mabl) }}</span>
                </td>
                <!-- Detail Link for Daily Report -->
                <button @click="openDailyDetail(item)" class="detail-link-btn">
                  <span class="icon">🔍</span>
                  {{ $t('employer.dashboard.viewDetails') }}
                </button>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Detail Modal -->
      <div v-if="detailModalVisible" class="modal-overlay" @click.self="detailModalVisible = false">
        <div class="modal-content detail-modal">
          <div class="modal-header">
            <h3>{{ selectedDetailTitle }}</h3>
            <button class="close-btn" @click="detailModalVisible = false">&times;</button>
          </div>

          <div class="modal-body">
            <div v-if="detailLoading" class="currency-loading">
              <div class="loading-spinner"></div>
            </div>

            <div v-else-if="detailData.length === 0" class="no-data">
              {{ $t('common.noData') }}
            </div>

            <div v-else class="report-table-container">
              <table class="report-table detail-table" style="direction: ltr;">
                <thead>
                  <tr>
                    <th v-if="showInvoiceColumn">{{ $t('employer.dashboard.dailyReport.detail.invoice') }}</th>
                    <th v-if="showExhibitColumn">{{ $t('employer.dashboard.dailyReport.detail.exhibit') }}</th>
                    <th>{{ $t('employer.dashboard.dailyReport.detail.customer') }}</th>
                    <th v-if="showPriceColumn">{{ $t('employer.dashboard.dailyReport.detail.price') }}</th>
                    <th v-if="showPriceDecColumn">{{ $t('employer.dashboard.dailyReport.detail.priceDec') }}</th>
                    <th v-if="showPriceIncColumn">{{ $t('employer.dashboard.dailyReport.detail.priceInc') }}</th>
                    <th v-if="showPriceAllColumn">{{ $t('employer.dashboard.dailyReport.detail.priceAll') }}</th>
                    <th>{{ $t('employer.dashboard.dailyReport.detail.currency') }}</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="row in detailData" :key="row.Invoice_Auto || row.Exhibit_Auto || row.cost">
                    <td
                      v-if="showInvoiceColumn"
                      :data-label="$t('employer.dashboard.dailyReport.detail.invoice')"
                      class="amount-cell"
                    >
                      <span class="detail-item">{{ formatCurrencyPrice(row.Invoice_Auto) }}</span>
                    </td>

                    <td
                      v-if="showExhibitColumn"
                      :data-label="$t('employer.dashboard.dailyReport.detail.exhibit')"
                      class="amount-cell"
                    >
                      <span class="detail-item">{{ formatCurrencyPrice(row.Exhibit_Auto) }}</span>
                    </td>

                    <td
                      class="amount-cell"
                      :data-label="$t('employer.dashboard.dailyReport.detail.customer')"
                    >
                      <span class="detail-item">{{ row.cost }}</span>
                    </td>

                    <td
                      v-if="showPriceColumn"
                      :data-label="$t('employer.dashboard.dailyReport.detail.price')"
                      class="amount-cell"
                    >
                      <span class="detail-item">{{ formatCurrencyPrice(row.Price) }}</span>
                    </td>

                    <td
                      v-if="showPriceDecColumn"
                      :data-label="$t('employer.dashboard.dailyReport.detail.priceDec')"
                      class="amount-cell"
                    >
                      <span class="detail-item">{{ formatCurrencyPrice(row.PriceDec) }}</span>
                    </td>

                    <td
                      v-if="showPriceIncColumn"
                      :data-label="$t('employer.dashboard.dailyReport.detail.priceInc')"
                      class="amount-cell"
                    >
                      <span class="detail-item">{{ formatCurrencyPrice(row.PriceInc) }}</span>
                    </td>

                    <td
                      v-if="showPriceAllColumn"
                      :data-label="$t('employer.dashboard.dailyReport.detail.priceAll')"
                      class="amount-cell"
                    >
                      <span class="detail-item">{{ formatCurrencyPrice(row.PriceAll) }}</span>
                    </td>

                    <td
                      :data-label="$t('employer.dashboard.dailyReport.detail.currency')"
                      class="currency-cell amount-cell"
                    >
                      <span class="detail-item">{{ row.Arz }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

          </div>
        </div>
      </div>

      <!-- Today Taraz Section -->
      <div v-if="hasPermission('ChartDailyActivity')" class="today-taraz-section">
        <div class="currency-header">
          <h3 style="margin-right: 5px; color: white;">
            {{ $t('employer.dashboard.todayTaraz.title') }}
          </h3>
          <div class="actions-group">
            <button class="toggle-btn" @click="todayTarazCollapsed = !todayTarazCollapsed"
              :title="todayTarazCollapsed ? $t('employer.dashboard.showMore') : $t('employer.dashboard.showLess')">
              <span class="toggle-icon" :class="{ collapsed: todayTarazCollapsed }">^</span>
              <span class="toggle-text">{{ todayTarazCollapsed ? $t('employer.dashboard.showMore') : $t('employer.dashboard.showLess') }}</span>
            </button>
            
            <button class="refresh-btn" @click="fetchTodayTaraz" :disabled="tarazLoading">
              <span class="refresh-icon">↻</span>
            </button>
          </div>
        </div>
        <DatePicker
            v-model="filters.dateTo"
            format="jYYYY/jMM/jDD"
            display-format="jYYYY/jMM/jDD"
            :placeholder="$t('employer.dashboard.selectDate')"
            :editable="true"
            input-class="persian-date-picker"
            :auto-submit="true"
            color="var(--primary)" 
          />

        <!-- CM Level Selector -->
        <div v-if="!todayTarazCollapsed" class="cm-level-selector today-taraz-filter" style="direction: rtl;">
          <label for="today-taraz-cm-select" class="cm-label">
            {{ $t('employer.traz.display_level') }}:
          </label>

          <select
            id="today-taraz-cm-select"
            v-model="selectedTodayTarazCmLevel"
            @change="onTodayTarazCmLevelChange"
            class="cm-select"
            :disabled="tarazLoading"
          >
            <option value="1">{{ $t('employer.traz.level_group') }}</option>
            <option value="2">{{ $t('employer.traz.level_total') }}</option>
            <option value="3">{{ $t('employer.traz.level_sub') }}</option>
            <option value="4">{{ $t('employer.traz.level_detail') }}</option>
          </select>
        </div>

        <div v-if="tarazLoading" class="currency-loading">
          <div class="loading-spinner"></div>
        </div>

        <div v-else-if="tarazError" class="currency-error">
          <span>{{ tarazError }}</span>
        </div>

        <div v-else-if="!todayTarazCollapsed && filteredTodayTarazData.length > 0" class="report-table-container">
          <table class="report-table taraz-table">
            <thead>
              <tr>
                <th>{{ $t('employer.dashboard.todayTaraz.handTitle') }}</th>
                <th>{{ $t('employer.dashboard.todayTaraz.bed') }}</th>
                <th>{{ $t('employer.dashboard.todayTaraz.bes') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in filteredTodayTarazData" :key="index">
                <td class="title-cell" style="--data-label-width: auto;" :data-label="item.HandTitle">
                  <span class="desktop-text">{{ item.HandTitle }}</span>
                </td>
                <td class="amount-cell" :data-label="$t('employer.dashboard.todayTaraz.bed')">
                  <div class="price-container">
                    <span class="price-item bed"> {{ formatCurrencyPrice(item.Sum_Bed) }}</span>
                  </div>
                </td>
                <td class="amount-cell" :data-label="$t('employer.dashboard.todayTaraz.bes')">
                  <div class="price-container">
                    <span class="price-item bes"> {{ formatCurrencyPrice(item.Sum_Bes) }}</span>
                  </div>
                </td>
                <!-- Detail Link for Daily Report -->
                <button class="detail-link-btn">
                  <span class="icon">🔍</span>
                  {{ $t('employer.dashboard.viewDetails') }}
                </button>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else-if="!todayTarazCollapsed && filteredTodayTarazData.length <= 0" class="currency-empty">
          <span class="empty-icon">📊</span>
          <span>{{ $t('employer.dashboard.todayTaraz.empty') }}</span>
        </div>
      </div>

      <!-- Monthly Activity Report Section -->
      <div v-if="hasPermission('ChartMonthlyActivity')" class="monthly-activity-section">
          <div class="currency-header">
              <h3 style="margin-right: 5px; color: white;">
                  {{ $t('employer.dashboard.monthlyActivity.title') }}
              </h3>

              <div class="actions-group">
                  <button class="toggle-btn"
                          @click="monthlyCollapsed = !monthlyCollapsed"
                          :title="monthlyCollapsed ? $t('employer.dashboard.showMore') : $t('employer.dashboard.showLess')">
                      <span class="toggle-icon" :class="{ collapsed: monthlyCollapsed }">^</span>
                      <span class="toggle-text">{{ monthlyCollapsed ? $t('employer.dashboard.showMore') : $t('employer.dashboard.showLess') }}</span>
                  </button>

                  <button class="refresh-btn" @click="fetchMonthlyReport" :disabled="monthlyLoading || !selectedAct">
                      <span class="refresh-icon">↻</span>
                  </button>
              </div>
          </div>

          <!-- Act Selector -->
          <div v-if="!monthlyCollapsed" class="monthly-selector">
              <select 
                  v-model="selectedAct" 
                  @change="onActChange"
                  class="act-select"
                  :disabled="monthlyLoading"
              >
                  <option value="">{{ $t('employer.dashboard.monthlyActivity.selectPlaceholder') }}</option>
                  <option 
                      v-for="act in acts" 
                      :key="act.Act" 
                      :value="act.Act"
                  >
                      {{ act.Act_Title }} ({{ act.Act }})
                  </option>
              </select>
          </div>

          <div v-if="monthlyLoading" class="currency-loading">
              <div class="loading-spinner"></div>
          </div>

          <div v-else-if="monthlyError" class="currency-error">
              <span>{{ monthlyError }}</span>
          </div>

          <!-- Placeholder when no act is selected -->
          <div v-else-if="!monthlyCollapsed && !selectedAct && !monthlyLoading && !monthlyError" class="monthly-placeholder">
              <span class="placeholder-icon">👆</span>
              <span>{{ $t('employer.dashboard.monthlyActivity.selectHint') }}</span>
          </div>

          <div v-else-if="!monthlyCollapsed && monthlyData.length > 0" class="monthly-content">
              <!-- Chart -->
              <div class="chart-box">
                  <div class="chart-header">
                      <div class="chart-title">
                          {{ monthlyActTitle || $t('employer.dashboard.monthlyActivity.chartTitle') }}
                      </div>

                      <select class="chart-type-select" v-model="monthlyChartType">
                          <option value="bar">{{ $t('employer.dashboard.dailySale.bar') }}</option>
                          <option value="line">{{ $t('employer.dashboard.dailySale.line') }}</option>
                          <option value="pie">{{ $t('employer.dashboard.dailySale.pie') }}</option>
                      </select>
                  </div>

                  <div class="chart-container">
                      <component
                          :is="chartComponentMap[monthlyChartType]"
                          :chartData="monthlyChartData"
                      />
                  </div>
              </div>

              <!-- Monthly Data Table -->
              <!-- <div class="monthly-table-container">
                  <table class="monthly-table">
                      <thead>
                          <tr>
                              <th>{{ $t('employer.dashboard.monthlyActivity.month') }}</th>
                              <th>{{ $t('employer.dashboard.monthlyActivity.amount') }}</th>
                          </tr>
                      </thead>
                      <tbody>
                          <tr v-for="item in monthlyData" :key="item.Mah">
                              <td>{{ getMonthName(item.Mah) }}</td>
                              <td class="amount-cell">{{ formatCurrencyPrice(item.Mabl) }}</td>
                          </tr>
                      </tbody>
                  </table>
              </div> -->
          </div>

          <div v-else-if="!monthlyCollapsed" class="currency-empty">
              <span class="empty-icon">📊</span>
              <span>{{ $t('employer.dashboard.monthlyActivity.empty') }}</span>
          </div>
      </div>
      
      <!-- Monthly Sood Chart Section -->
      <div v-if="hasPermission('ChartMonthlySood')" class="monthly-activity-section">
          <div class="currency-header">
              <h3 style="margin-right: 5px; color: white;">
                  {{ $t('employer.dashboard.monthlySood.title') }}
              </h3>

              <div class="actions-group">
                  <button class="toggle-btn"
                          @click="monthlySoodCollapsed = !monthlySoodCollapsed"
                          :title="monthlySoodCollapsed ? $t('employer.dashboard.showMore') : $t('employer.dashboard.showLess')">
                      <span class="toggle-icon" :class="{ collapsed: monthlySoodCollapsed }">^</span>
                      <span class="toggle-text">{{ monthlySoodCollapsed ? $t('employer.dashboard.showMore') : $t('employer.dashboard.showLess') }}</span>
                  </button>

                  <button class="refresh-btn" @click="fetchMonthlySoodReport" :disabled="monthlySoodLoading">
                      <span class="refresh-icon">↻</span>
                  </button>
              </div>
          </div>

          <div v-if="monthlySoodLoading" class="currency-loading">
              <div class="loading-spinner"></div>
          </div>

          <div v-else-if="monthlySoodError" class="currency-error">
              <span>{{ monthlySoodError }}</span>
          </div>

          <div v-else-if="!monthlySoodCollapsed && monthlySoodData.length > 0" class="monthly-content">
              <!-- Chart -->
              <div class="chart-box">
                  <div class="chart-header">
                      <div class="chart-title">
                          {{ $t('employer.dashboard.monthlySood.chartTitle') }}
                      </div>

                      <select class="chart-type-select" v-model="monthlySoodChartType">
                          <option value="bar">{{ $t('employer.dashboard.dailySale.bar') }}</option>
                          <option value="line">{{ $t('employer.dashboard.dailySale.line') }}</option>
                          <option value="pie">{{ $t('employer.dashboard.dailySale.pie') }}</option>
                      </select>
                  </div>

                  <div class="chart-container">
                      <component
                          :is="chartComponentMap[monthlySoodChartType]"
                          :chartData="monthlySoodChartData"
                      />
                  </div>
              </div>
          </div>

          <div v-else-if="!monthlySoodCollapsed" class="currency-empty">
              <span class="empty-icon">📊</span>
              <span>{{ $t('employer.dashboard.monthlySood.empty') }}</span>
          </div>
      </div>
    
      <!-- Card Services Section -->
      <div class="horizontal-services-section">
        
        <h3 class="section-title">
          {{ $t('employer.dashboard.services.title') }}
        </h3>

        <!-- First Row -->
        <div class="horizontal-scroll-container">
          <div class="scroll-content">
            
            <div v-if="hasPermission('Traz')" class="scroll-service-item" @click="navigateTraz">
              <div class="scroll-service-icon">
                <img src="@/assets/Icons/invoice.png" />
              </div>
              <span class="scroll-service-label">
                {{ $t('employer.dashboard.services.balance') }}
              </span>
            </div>

            <div v-if="hasPermission('TahlilTraz')" class="scroll-service-item" @click="navigateAnalysisTraz">
              <div class="scroll-service-icon">
                <img src="@/assets/Icons/analytics.png" />
              </div>
              <span class="scroll-service-label">
                {{ $t('employer.dashboard.services.aiBalanceAnalysis') }}
              </span>
            </div>

            <div v-if="hasPermission('SoratHesab')" class="scroll-service-item" @click="navigateCustomerSearch">
              <div class="scroll-service-icon">
                <img src="@/assets/Icons/searchCustomer.png" />
              </div>
              <span class="scroll-service-label">
                {{ $t('employer.dashboard.services.customerInvoice') }}
              </span>
            </div>

          </div>
        </div>

        <!-- Second Row -->
        <div class="horizontal-scroll-container">
          <div class="scroll-content">

            <div v-if="hasPermission('ReportMande')" class="scroll-service-item" @click="navigateMande">
              <div class="scroll-service-icon">
                <img src="@/assets/Icons/reportMande.png" />
              </div>
              <span class="scroll-service-label">
                {{ $t('employer.dashboard.services.balanceReport') }}
              </span>
            </div>

            <div v-if="hasPermission('ReportMande2')" class="scroll-service-item" @click="navigateMande2">
              <div class="scroll-service-icon">
                <img src="@/assets/Icons/reportMande.png" />
              </div>
              <span class="scroll-service-label">
                {{ $t('employer.dashboard.services.balanceReport2') }}
              </span>
            </div>

            <div v-if="hasPermission('Taseer')" class="scroll-service-item" @click="navigateTaseer">
              <div class="scroll-service-icon">
                <img src="@/assets/Icons/taseer.png" />
              </div>
              <span class="scroll-service-label">
                {{ $t('employer.dashboard.services.realTimeExchangeRate') }}
              </span>
            </div>

          </div>
        </div>

        <!-- Third Row -->
        <div class="horizontal-scroll-container">
          <div class="scroll-content">

            <div v-if="hasPermission('LiquidyRatio')" class="scroll-service-item" @click="navigateLiquidyRatio">
              <div class="scroll-service-icon">
                <img src="@/assets/Icons/funds.png" />
              </div>
              <span class="scroll-service-label">
                {{ $t('employer.dashboard.services.liquidyRatio') }}
              </span>
            </div>
            <div class="scroll-service-item" @click="navigateKalaSearch">
              <div class="scroll-service-icon">
                <img src="@/assets/Icons/product.png" />
              </div>
              <span class="scroll-service-label">
                {{ $t('employer.dashboard.services.kalaSearch') }}
              </span>
            </div>
            <div v-if="hasPermission('Traz')" class="scroll-service-item" @click="navigateProfitLoss">
                <div class="scroll-service-icon">
                    <img src="@/assets/Icons/funds.png" />
                </div>
                <span class="scroll-service-label">
                    {{ $t('employer.dashboard.services.profitLoss') }}
                </span>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import Navbar from '@/components/Navbar.vue'
import { useRouter } from 'vue-router'
import { employerService } from '@/services/api'
import moment from 'moment-jalaali'
import BarChart from '@/components/charts/BarChart.vue'
import LineChart from '@/components/charts/LineChart.vue'
import PieChart from '@/components/charts/PieChart.vue'
import DatePicker from 'vue3-persian-datetime-picker'

export default {
  name: 'BankDashboard',
  components: {
    Navbar,
    BarChart,
    LineChart,
    PieChart,
    DatePicker
  },
  setup() {
    const authStore = useAuthStore()
    const router = useRouter()

    // ============= STATE =============
    const remainingCash = ref(0)
    const loading = ref(false)
    const error = ref(null)

    // Currency
    const currencyPrices = ref([])
    const currencyLoading = ref(false)
    const currencyError = ref(null)
    const refreshingCurrencies = ref(false)
    const showAllCurrencies = ref(false)
    const currencyUpdating = ref({})
    const currencyFetched = ref(false)

    // Daily Report
    const dailyReportData = ref([])
    const reportLoading = ref(false)
    const reportError = ref(null)
    const dailyReportFetched = ref(false)

    // Today Taraz
    const todayTarazData = ref([])
    const tarazLoading = ref(false)
    const tarazError = ref(null)
    const selectedTodayTarazCmLevel = ref('1')
    const todayTarazFetched = ref(false)

    // Daily Sale
    const dailySaleData = ref([])
    const dailySaleLoading = ref(false)
    const dailySaleError = ref(null)
    const dailySaleFetched = ref(false)

    // Detail Modal
    const detailModalVisible = ref(false)
    const detailLoading = ref(false)
    const detailData = ref([])
    const selectedDetailTitle = ref('')

    // Chart types
    const dailySaleChartTypeV = ref('bar')
    const dailySaleChartTypeM = ref('pie')

    // Monthly Activity Report
    const acts = ref([])
    const selectedAct = ref(null)
    const monthlyData = ref([])
    const monthlyLoading = ref(false)
    const monthlyError = ref(null)
    const monthlyFetched = ref(false)
    const monthlyChartType = ref('line')
    const monthlyActTitle = ref('')

    const monthlySoodData = ref([])
    const monthlySoodLoading = ref(false)
    const monthlySoodError = ref(null)
    const monthlySoodFetched = ref(false)
    const monthlySoodCollapsed = ref('false')
    const monthlySoodChartType = ref('line')

    const monthlyCollapsed = ref('false')

    const monthlyChartData = computed(() => {
        const labels = monthlyData.value.map(item => getMonthName(item.Mah))
        const data = monthlyData.value.map(item => item.Mabl || 0)
        
        const colors = [
            '#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#a855f7',
            '#06b6d4', '#f97316', '#84cc16', '#e11d48', '#14b8a6'
        ]
        
        const bgColors = labels.map((_, i) => {
            const color = colors[i % colors.length]
            return color + '40'
        })
        
        const borderColors = labels.map((_, i) => colors[i % colors.length])

        return {
            labels,
            datasets: [{
                label: monthlyActTitle.value || 'مبلغ',
                data,
                backgroundColor: bgColors,
                borderColor: borderColors,
                borderWidth: 2,
                pointBackgroundColor: borderColors,
                pointBorderColor: '#fff',
                pointRadius: 4,
                tension: 0.35,
                fill: true,
            }]
        }
    })

    const fetchActs = async () => {
        monthlyLoading.value = true
        try {
            const response = await employerService.getActs()
            if (response.data.success) {
                acts.value = response.data.data || []
            }
        } catch (err) {
            console.error('Error fetching acts:', err)
        } finally {
            monthlyLoading.value = false
        }
    }

    const fetchMonthlyReport = async () => {
        if (!selectedAct.value) {
            monthlyData.value = []
            monthlyActTitle.value = ''
            return
        }

        monthlyLoading.value = true
        monthlyError.value = null

        try {
            const response = await employerService.getMonthlyActivityReport(selectedAct.value)
            
            if (response.data.success) {
                monthlyData.value = response.data.data || []
                monthlyActTitle.value = response.data.act_title || ''
                monthlyFetched.value = true
            } else {
                monthlyError.value = response.data.message || 'خطا در دریافت اطلاعات'
            }
        } catch (err) {
            console.error('Error fetching monthly report:', err)
            monthlyError.value = err.response?.data?.detail || err.message || 'خطا در اتصال به سرور'
        } finally {
            monthlyLoading.value = false
        }
    }

    const onActChange = () => {
        fetchMonthlyReport()
    }

    const getMonthName = (monthNumber) => {
        const months = {
            '01': 'فروردین',
            '1': 'فروردین',
            '02': 'اردیبهشت',
            '2': 'اردیبهشت',
            '03': 'خرداد',
            '3': 'خرداد',
            '04': 'تیر',
            '4': 'تیر',
            '05': 'مرداد',
            '5': 'مرداد',
            '06': 'شهریور',
            '6': 'شهریور',
            '07': 'مهر',
            '7': 'مهر',
            '08': 'آبان',
            '8': 'آبان',
            '09': 'آذر',
            '9': 'آذر',
            '10': 'دی',
            '11': 'بهمن',
            '12': 'اسفند'
        }
        return months[monthNumber] || monthNumber
    }

    // Add to watchers
    watch(monthlyCollapsed, (newVal) => {
        localStorage.setItem(STORAGE_KEYS.MONTHLY, String(newVal))
        if (!newVal && !monthlyFetched.value && !monthlyLoading.value) {
            if (!acts.value.length) {
                fetchActs()
            }
            if (selectedAct.value) {
                fetchMonthlyReport()
            }
        }
    })

    // Popular currencies
    const popularCurrencies = ['USD', 'EUR', 'AED', 'CNY']

    // Filters
    const filters = ref({
      dateFrom: moment().format('jYYYY/jMM/jDD'),
      dateTo: moment().format('jYYYY/jMM/jDD')
    })

    // ============= STORAGE KEYS =============
    const STORAGE_KEYS = {
      CURRENCY: 'dashboard_currency_collapsed',
      DAILY_SALE: 'dashboard_daily_sale_collapsed',
      DAILY_REPORT: 'dashboard_daily_report_collapsed',
      TODAY_TARAZ: 'dashboard_today_taraz_collapsed',
      MONTHLY: 'dashboard_monthly_collapsed',
      MONTHLY_SOOD: 'dashboard_monthly_sood_collapsed' 
    }

    // Default: collapsed (true) - sections are hidden by default
    const currencyPriceCollapsed = ref('false')
    const dailySaleCollapsed = ref('false')
    const dailyReportCollapsed = ref('false')
    const todayTarazCollapsed = ref('false')
    

    // ============= PALETTE =============
    const palette = [
      '#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#a855f7',
      '#06b6d4', '#f97316', '#84cc16', '#e11d48', '#14b8a6'
    ]

    const withAlpha = (hex, a = 0.35) => {
      const h = hex.replace('#', '')
      const r = parseInt(h.slice(0, 2), 16)
      const g = parseInt(h.slice(2, 4), 16)
      const b = parseInt(h.slice(4, 6), 16)
      return `rgba(${r},${g},${b},${a})`
    }

    // ============= COMPUTED =============
    const displayedCurrencies = computed(() => {
      if (!currencyPrices.value || currencyPrices.value.length === 0) return []
      if (showAllCurrencies.value) return currencyPrices.value
      return currencyPrices.value.filter(currency => 
        currency && currency['کد ارز'] && popularCurrencies.includes(currency['کد ارز'])
      )
    })

    const filteredTodayTarazData = computed(() => {
      return (todayTarazData.value || []).filter((item) => {
        const bed = Number(String(item?.Sum_Bed ?? 0).replace(/,/g, '')) || 0
        const bes = Number(String(item?.Sum_Bes ?? 0).replace(/,/g, '')) || 0
        return !(bed === 0 && bes === 0)
      })
    })

    const dailySaleLabels = computed(() => (dailySaleData.value || []).map(x => x.name || ''))
    const dailySaleSumV = computed(() => (dailySaleData.value || []).map(x => Number(x.SumV || 0)))
    const dailySaleSumM = computed(() => (dailySaleData.value || []).map(x => Number(x.SumM || 0)))

    const dailySaleChartDataV = computed(() => {
      const labels = dailySaleLabels.value
      const data = dailySaleSumV.value
      const bgColors = labels.map((_, i) => withAlpha(palette[i % palette.length], 0.45))
      const borderColors = labels.map((_, i) => palette[i % palette.length])
      return {
        labels,
        datasets: [{
          label: 'تعداد (SumV)',
          data,
          backgroundColor: bgColors,
          borderColor: borderColors,
          borderWidth: 2,
          pointBackgroundColor: borderColors,
          pointBorderColor: '#fff',
          pointRadius: 3,
          tension: 0.35,
          fill: true,
        }]
      }
    })

    const dailySaleChartDataM = computed(() => {
      const labels = dailySaleLabels.value
      const data = dailySaleSumM.value
      const bgColors = labels.map((_, i) => withAlpha(palette[i % palette.length], 0.45))
      const borderColors = labels.map((_, i) => palette[i % palette.length])
      return {
        labels,
        datasets: [{
          label: 'مبلغ (SumM)',
          data,
          backgroundColor: bgColors,
          borderColor: borderColors,
          borderWidth: 2,
          pointBackgroundColor: borderColors,
          pointBorderColor: '#fff',
          pointRadius: 3,
          tension: 0.35,
          fill: true,
        }]
      }
    })

    const chartComponentMap = {
      bar: BarChart,
      line: LineChart,
      pie: PieChart,
    }

    // ============= DETAIL MODAL COMPUTED =============
    const showInvoiceColumn = computed(() => {
      return (detailData.value || []).some(row => toNumber(row?.Invoice_Auto) !== 0)
    })

    const showExhibitColumn = computed(() => {
      return (detailData.value || []).some(row => toNumber(row?.Exhibit_Auto) !== 0)
    })

    const showPriceDecColumn = computed(() => {
      return (detailData.value || []).some(row => toNumber(row?.PriceDec) !== 0)
    })

    const showPriceIncColumn = computed(() => {
      return (detailData.value || []).some(row => toNumber(row?.PriceInc) !== 0)
    })

    const showPriceAllColumn = computed(() => {
      return (detailData.value || []).some(row => toNumber(row?.PriceAll) !== 0)
    })

    const showMonthlyPlaceholder = computed(() => {
      return !monthlyCollapsed.value && !selectedAct.value && !monthlyLoading.value && !monthlyError.value
    })

    const priceEqualsPriceAllForAllRows = computed(() => {
      const rows = detailData.value || []
      if (!rows.length) return false
      const meaningfulRows = rows.filter(row =>
        toNumber(row?.Price) !== 0 || toNumber(row?.PriceAll) !== 0
      )
      if (!meaningfulRows.length) return false
      return meaningfulRows.every(row =>
        toNumber(row?.Price) === toNumber(row?.PriceAll)
      )
    })

    const showPriceColumn = computed(() => {
      if (priceEqualsPriceAllForAllRows.value) return false
      return (detailData.value || []).some(row => toNumber(row?.Price) !== 0)
    })

    const showSumValueColumn = computed(() => {
      return (dailyReportData.value || []).some(r => {
        return toNumber(r?.Sum_Value) !== 0
      })
    })

    // Add computed for monthly sood chart data
const monthlySoodChartData = computed(() => {
    const labels = monthlySoodData.value.map(item => getMonthName(item.Mah))
    const data = monthlySoodData.value.map(item => item.Sood || 0)
        
        const colors = [
            '#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#a855f7',
            '#06b6d4', '#f97316', '#84cc16', '#e11d48', '#14b8a6'
        ]
        
        const bgColors = labels.map((_, i) => {
            const color = colors[i % colors.length]
            return color + '40'
        })
        
        const borderColors = labels.map((_, i) => colors[i % colors.length])

        return {
            labels,
            datasets: [{
              label: 'سود/زیان ماهانه',
              data,
              backgroundColor: bgColors,
              borderColor: borderColors,
              borderWidth: 2,
              pointBackgroundColor: borderColors,
              pointBorderColor: '#fff',
              pointRadius: 4,
              tension: 0.35,
              fill: true,
          }]
        }
})

// Add computed for total monthly sood
const totalMonthlySood = computed(() => {
    return monthlySoodData.value.reduce((sum, item) => sum + (item.Sood || 0), 0)
})

// Add fetch function for monthly sood
const fetchMonthlySoodReport = async () => {
    if (!authStore.user?.customerId) return
    if (monthlySoodLoading.value) return

    monthlySoodLoading.value = true
    monthlySoodError.value = null

    try {
        const response = await employerService.getMonthlySood()
        
        if (response.data.success) {
            monthlySoodData.value = response.data.data || []
            monthlySoodFetched.value = true
        } else {
            monthlySoodError.value = response.data.message || 'خطا در دریافت گزارش سود ماهانه'
        }
    } catch (err) {
        console.error('Error fetching monthly sood report:', err)
        monthlySoodError.value = err.response?.data?.detail || err.message || 'خطا در اتصال به سرور'
    } finally {
        monthlySoodLoading.value = false
    }
}

// Add watcher for monthly sood collapse
watch(monthlySoodCollapsed, (newVal) => {
    localStorage.setItem(STORAGE_KEYS.MONTHLY_SOOD, String(newVal))
    if (!newVal && !monthlySoodFetched.value && !monthlySoodLoading.value) {
        fetchMonthlySoodReport()
    }
})

    // ============= WATCHERS FOR LAZY LOADING =============
    watch(currencyPriceCollapsed, (newVal) => {
      localStorage.setItem(STORAGE_KEYS.CURRENCY, String(newVal))
      if (!newVal && !currencyFetched.value && !currencyLoading.value) {
        fetchCurrenciesPrice()
      }
    })

    watch(dailySaleCollapsed, (newVal) => {
      localStorage.setItem(STORAGE_KEYS.DAILY_SALE, String(newVal))
      if (!newVal && !dailySaleFetched.value && !dailySaleLoading.value && filters.value.dateTo) {
        fetchDailySaleReport()
      }
    })

    watch(dailyReportCollapsed, (newVal) => {
      localStorage.setItem(STORAGE_KEYS.DAILY_REPORT, String(newVal))
      if (!newVal && !dailyReportFetched.value && !reportLoading.value && filters.value.dateTo) {
        fetchDailyReport()
      }
    })

    watch(todayTarazCollapsed, (newVal) => {
      localStorage.setItem(STORAGE_KEYS.TODAY_TARAZ, String(newVal))
      if (!newVal && !todayTarazFetched.value && !tarazLoading.value && filters.value.dateTo) {
        fetchTodayTaraz()
      }
    })

    watch(() => filters.value.dateTo, () => {
      if (!currencyPriceCollapsed.value && currencyFetched.value) {
        fetchCurrenciesPrice()
      }
      if (!dailySaleCollapsed.value && dailySaleFetched.value) {
        fetchDailySaleReport()
      }
      if (!dailyReportCollapsed.value && dailyReportFetched.value) {
        fetchDailyReport()
      }
      if (!todayTarazCollapsed.value && todayTarazFetched.value) {
        fetchTodayTaraz()
      }
    })

    // ============= HELPER FUNCTIONS =============
    const toNumber = (value) => {
      if (value === null || value === undefined) return 0
      let str = String(value).trim()
      const persianDigits = '۰۱۲۳۴۵۶۷۸۹'
      const arabicDigits = '٠١٢٣٤٥٦٧٨٩'
      str = str.replace(/[۰-۹]/g, d => persianDigits.indexOf(d))
              .replace(/[٠-٩]/g, d => arabicDigits.indexOf(d))
      str = str.replace(/[,\u060C\u066C]/g, '')
      const num = Number(str)
      return Number.isFinite(num) ? num : 0
    }

    const hasPermission = (permissionKey) => {
      return Array.isArray(authStore.permissions)
        ? authStore.permissions.includes(permissionKey)
        : false
    }

    const isPopularCurrency = (code) => {
      return code && popularCurrencies.includes(code)
    }

    const getCurrencyFlag = (code) => {
      const flagMap = {
        'USD': '🇺🇸', 'EUR': '🇪🇺', 'GBP': '🇬🇧', 'CAD': '🇨🇦',
        'TRY': '🇹🇷', 'AED': '🇦🇪', 'JPY': '🇯🇵', 'AUD': '🇦🇺',
        'NZD': '🇳🇿', 'CHF': '🇨🇭', 'PKR': '🇵🇰', 'AZN': '🇦🇿',
        'NOK': '🇳🇴', 'SEK': '🇸🇪', 'DKK': '🇩🇰', 'KWD': '🇰🇼',
        'OMR': '🇴🇲', 'RUB': '🇷🇺', 'BRL': '🇧🇷', 'THB': '🇹🇭',
        'AFN': '🇦🇫', 'INR': '🇮🇳', 'CNY': '🇨🇳', 'MYR': '🇲🇾'
      }
      return flagMap[code] || '💰'
    }

    const formatCurrencyPrice = (price) => {
      if (!price || price === "0" || price === 0) return '۰'
      try {
        const cleanPrice = price.toString().replace(/[,\u066B\u066C\u060C]/g, '')
        const numericStr = cleanPrice.replace(/[^\d.-]/g, '')
        const numPrice = parseFloat(numericStr)
        if (isNaN(numPrice)) return '۰'
        return numPrice.toLocaleString('fa-IR', {
          maximumFractionDigits: 0,
          minimumFractionDigits: 0
        })
      } catch (error) {
        return formatPriceManually(price)
      }
    }

    const formatPriceManually = (price) => {
      try {
        const priceStr = price.toString().replace(/[^0-9.-]/g, '')
        const parts = priceStr.split('.')
        let integerPart = parts[0]
        integerPart = integerPart.replace(/^0+/, '') || '0'
        const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']
        let result = ''
        let count = 0
        for (let i = integerPart.length - 1; i >= 0; i--) {
          const digit = parseInt(integerPart[i])
          if (!isNaN(digit)) {
            result = persianDigits[digit] + result
            count++
            if (count === 3 && i > 0) {
              result = '،' + result
              count = 0
            }
          }
        }
        return result || '۰'
      } catch {
        return '۰'
      }
    }

    const formatCurrencyTime = () => {
      try {
        const formatter = new Intl.DateTimeFormat('fa-IR', {
          timeZone: 'Asia/Tehran',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })
        return formatter.format(new Date())
      } catch {
        return '--:--'
      }
    }

    const formatRemainingCash = (amount) => {
      if (!amount && amount !== 0) return '۰'
      const absoluteAmount = Math.abs(amount)
      return new Intl.NumberFormat('fa-IR').format(absoluteAmount) + ' ریال'
    }

    const formatBestanKari = (amount) => {
      if (!amount && amount !== 0) return '۰'
      return amount > 0 ? 'بدهکار' : 'بستانکار'
    }

    const formatPersianNumber = (val) => new Intl.NumberFormat('fa-IR').format(val)

    // ============= FETCH FUNCTIONS =============
    const fetchCurrenciesPrice = async () => {
      if (!authStore.user?.customerId) return
      if (currencyLoading.value) return

      currencyLoading.value = true
      currencyError.value = null

      try {
        const response = await employerService.getCurrenciesPrice()
        if (response.data && response.data.success) {
          const prices = response.data.currency_prices || []
          currencyPrices.value = Array.isArray(prices) ? prices : []
          currencyFetched.value = true
          if (currencyPrices.value.length > 0) {
            currencyPrices.value.sort((a, b) => {
              const aIsPopular = popularCurrencies.includes(a['کد ارز'])
              const bIsPopular = popularCurrencies.includes(b['کد ارز'])
              if (aIsPopular && !bIsPopular) return -1
              if (!aIsPopular && bIsPopular) return 1
              return 0
            })
          }
        } else {
          currencyError.value = response.data?.message || 'خطا در دریافت داده‌ها'
        }
      } catch (err) {
        console.error('Error fetching currency prices:', err)
        currencyError.value = err.response?.data?.detail || 'خطا در ارتباط با سرور'
      } finally {
        currencyLoading.value = false
        refreshingCurrencies.value = false
      }
    }

    const refreshCurrencyPrices = async () => {
      if (refreshingCurrencies.value || currencyLoading.value) return
      refreshingCurrencies.value = true
      currencyFetched.value = false
      setTimeout(async () => {
        await fetchCurrenciesPrice()
        Object.keys(currencyUpdating.value).forEach(key => {
          currencyUpdating.value[key] = false
        })
      }, 300)
    }

    const fetchDailyReport = async () => {
      if (!authStore.user?.customerId) return
      if (reportLoading.value) return

      reportLoading.value = true
      reportError.value = null

      try {
        const reportDate = filters.value.dateTo
        const response = await employerService.getDailyActivityReport(reportDate)
        if (response.data && response.data.success) {
          dailyReportData.value = response.data.data || []
          dailyReportFetched.value = true
        } else {
          reportError.value = response.data?.message || 'خطا در دریافت گزارش فعالیت'
        }
      } catch (err) {
        console.error('Error fetching daily report:', err)
        reportError.value = err.response?.data?.detail || 'خطا در دریافت گزارش فعالیت روزانه'
      } finally {
        reportLoading.value = false
      }
    }

    const refreshDailyReport = async () => {
      if (reportLoading.value) return
      dailyReportFetched.value = false
      await fetchDailyReport()
    }

    const fetchTodayTaraz = async () => {
      if (!authStore.user?.customerId) return
      if (tarazLoading.value) return

      tarazLoading.value = true
      tarazError.value = null

      try {
        const todayJalali = filters.value.dateTo
        const payload = {
          arz: 1,
          cm: Number(selectedTodayTarazCmLevel.value),
          mah: 0,
          date_from: todayJalali,
          date_to: todayJalali,
          include_summary: true
        }
        const response = await employerService.getTodayTaraz(payload)
        if (response?.data?.success) {
          todayTarazData.value = response.data.data || []
          todayTarazFetched.value = true
        } else {
          tarazError.value = response?.data?.message || 'خطا در دریافت تراز امروز'
        }
      } catch (err) {
        console.error('Error fetching today taraz:', err)
        tarazError.value = err.response?.data?.detail || err.message || 'خطا در دریافت تراز امروز'
      } finally {
        tarazLoading.value = false
      }
    }

    const refreshTodayTaraz = async () => {
      if (tarazLoading.value) return
      todayTarazFetched.value = false
      await fetchTodayTaraz()
    }

    const fetchDailySaleReport = async () => {
      if (!authStore.user?.customerId) return
      if (dailySaleLoading.value) return

      dailySaleLoading.value = true
      dailySaleError.value = null

      try {
        const reportDate = filters.value.dateTo
        const response = await employerService.getDailySaleReport(reportDate)
        if (response?.data?.success) {
          dailySaleData.value = response.data.data || []
          dailySaleFetched.value = true
        } else {
          dailySaleError.value = response?.data?.message || 'خطا در دریافت گزارش فروش روزانه'
        }
      } catch (err) {
        console.error('Error fetching daily sale report:', err)
        dailySaleError.value = err.response?.data?.detail || err.message || 'خطا در دریافت گزارش فروش روزانه'
      } finally {
        dailySaleLoading.value = false
      }
    }

    const refreshDailySale = async () => {
      if (dailySaleLoading.value) return
      dailySaleFetched.value = false
      await fetchDailySaleReport()
    }

    // ============= OTHER FUNCTIONS =============
    const onTodayTarazCmLevelChange = async () => {
      todayTarazFetched.value = false
      await fetchTodayTaraz()
    }

    const openDailyDetail = (item) => {
        // Navigate to detail page instead of opening modal
        router.push({
            name: 'DailyReportDetail',
            params: { act: item.Act },
            query: { 
                title: item.Act_Title,
                date: filters.value.dateTo
            }
        })
    }

    // ============= NAVIGATION =============
    const navigateTraz = () => router.push('/employer/traz')
    const navigateMande = () => router.push('/employer/report-mande')
    const navigateMande2 = () => router.push('/employer/report-mande2')
    const navigateTaseer = () => router.push('/employer/taseer-sood')
    const navigateOperator = () => router.push('/admin/operator-management')
    const navigateAnalysisTraz = () => router.push('/employer/analysis-traz')
    const navigateCustomerSearch = () => router.push('/employer/customer-search')
    const navigateLiquidyRatio = () => router.push('/employer/liquidy-ratio')
    const navigateKalaSearch = () => router.push('/employer/kala-search')
    const navigateProfitLoss  = () => router.push('/employer/traz-sood')

    const goBack = () => router.push('/select-role')

    // ============= LIFECYCLE =============
    onMounted(async () => {
      if (authStore.isAuthenticated) {
        // Only fetch if sections are expanded on load
        const promises = []
        if (!currencyPriceCollapsed.value) promises.push(fetchCurrenciesPrice())
        if (!dailySaleCollapsed.value) promises.push(fetchDailySaleReport())
        if (!dailyReportCollapsed.value) promises.push(fetchDailyReport())
        if (!todayTarazCollapsed.value) promises.push(fetchTodayTaraz())
        if (promises.length > 0) {
          await Promise.all(promises)
        }
        if (!monthlySoodCollapsed.value) promises.push(fetchMonthlySoodReport()) // Add this
      }
    })

    // ============= RETURN =============
    return {
      // State
      remainingCash,
      loading,
      error,
      
      // Currency
      currencyPrices,
      currencyLoading,
      currencyError,
      refreshingCurrencies,
      showAllCurrencies,
      displayedCurrencies,
      popularCurrencies,
      currencyFetched,
      currencyPriceCollapsed,
      
      // Daily Report
      dailyReportData,
      reportLoading,
      reportError,
      dailyReportFetched,
      dailyReportCollapsed,
      showSumValueColumn,
      
      // Today Taraz
      todayTarazData,
      tarazLoading,
      tarazError,
      selectedTodayTarazCmLevel,
      todayTarazFetched,
      todayTarazCollapsed,
      filteredTodayTarazData,
      
      // Daily Sale
      dailySaleData,
      dailySaleLoading,
      dailySaleError,
      dailySaleFetched,
      dailySaleCollapsed,
      dailySaleChartTypeV,
      dailySaleChartTypeM,
      dailySaleChartDataV,
      dailySaleChartDataM,
      
      // Detail Modal
      detailModalVisible,
      detailLoading,
      detailData,
      selectedDetailTitle,
      showInvoiceColumn,
      showExhibitColumn,
      showPriceColumn,
      showPriceDecColumn,
      showPriceIncColumn,
      showPriceAllColumn,
      
      // Filters
      filters,
      
      // Methods
      fetchCurrenciesPrice,
      refreshCurrencyPrices,
      fetchDailyReport,
      refreshDailyReport,
      fetchTodayTaraz,
      refreshTodayTaraz,
      fetchDailySaleReport,
      refreshDailySale,
      onTodayTarazCmLevelChange,
      openDailyDetail,
      
      // Helpers
      hasPermission,
      isPopularCurrency,
      getCurrencyFlag,
      formatCurrencyPrice,
      formatCurrencyTime,
      formatRemainingCash,
      formatBestanKari,
      formatPersianNumber,
      toNumber,
      
      // Navigation
      navigateTraz,
      navigateMande,
      navigateMande2,
      navigateTaseer,
      navigateAnalysisTraz,
      navigateCustomerSearch,
      navigateOperator,
      navigateLiquidyRatio,
      navigateKalaSearch,
      navigateProfitLoss,
      goBack,
      
      // Charts
      chartComponentMap,

      acts,
      selectedAct,
      monthlyData,
      monthlyLoading,
      monthlyError,
      monthlyFetched,
      monthlyCollapsed,
      monthlyChartType,
      monthlyActTitle,
      monthlyChartData,
      fetchActs,
      fetchMonthlyReport,
      onActChange,
      getMonthName,
      showMonthlyPlaceholder,
      monthlySoodData,
      monthlySoodLoading,
      monthlySoodError,
      monthlySoodFetched,
      monthlySoodCollapsed,
      monthlySoodChartType,
      monthlySoodChartData,
      totalMonthlySood,
      fetchMonthlySoodReport,
    }
  }
}
</script>

<style scoped>
.back-arrow {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.back-arrow:hover {
  background-color: #f0f0f0;
}
.bank-app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f5f5f5;
  direction: rtl;
}

.main-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  padding-bottom: 80px; /* Space for bottom navigation */
}

.account-summary {
  background: linear-gradient(135deg, #4a6baf, #2c4a8c);
  color: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.account-type {
  font-size: 16px;
  margin-bottom: 8px;
  opacity: 0.9;
}

.account-balance {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.balance-label {
  font-size: 14px;
  opacity: 0.8;
}

.balance-amount {
  font-size: 18px;
  font-weight: bold;
  margin-left: auto;
  margin-right: auto;
}

.section-title {
  font-size: 16px;
  margin: 20px 0 12px;
  color: #333;
  font-weight: 600;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.service-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.service-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  font-size: 12px;
  color: #4a6baf;
  font-weight: bold;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.service-label {
  font-size: 12px;
  color: #555;
}

.additional-text {
  background-color: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  color: #555;
}

.account-number {
  margin-top: 8px;
  font-weight: bold;
  color: #4a6baf;
}

/* Horizontal Scroll Services */
.horizontal-services-section {
  margin-bottom: 24px;
}

.horizontal-scroll-container {
  overflow-x: auto;
  padding: 8px 0;
  margin: 0 -16px;
  padding: 8px 16px;
}

.horizontal-scroll-container::-webkit-scrollbar {
  display: none;
}

.scroll-content {
  display: flex;
  gap: 16px;
  padding: 4px 0;
  min-width: min-content;
}

.scroll-service-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 80px;
  flex-shrink: 0;
  margin-right: auto;
  margin-left: auto;
}

.scroll-service-item:hover {
  transform: scale(1.2);
  cursor: pointer;
  background-color: #f5f5f5;
  transform: translateY(-2px);
}

.scroll-service-item:active {
  transform: translateY(0);
}

.scroll-service-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  font-size: 12px;
  color: #4a6baf;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
  overflow: hidden; /* Prevent image overflow */
}

.scroll-service-icon img {
  max-width: 70%; /* Limit image to 70% of container */
  max-height: 70%; /* Limit image to 70% of container */
  object-fit: contain; /* Maintain aspect ratio */
}

.scroll-service-label {
  font-size: 11px;
  color: #555;
  line-height: 1.3;
  max-width: 80px;
}

/* Original Services List */
.services-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.service-card {
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.service-card .card-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 12px;
  font-size: 12px;
  color: #4a6baf;
  font-weight: bold;
}

.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-around;
  background-color: white;
  padding: 12px 0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 100;
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 12px;
  color: #555;
}

.nav-icon {
  width: 24px;
  height: 24px;
  margin-bottom: 4px;
  background-color: #e9ecef;
  border-radius: 50%;
}

.home-icon {
  background-color: #4a6baf;
}

.services-icon, .cards-icon, .profile-icon {
  background-color: #e9ecef;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .services-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }
  
  .service-icon {
    width: 50px;
    height: 50px;
    font-size: 10px;
  }
  
  .service-label {
    font-size: 10px;
  }

  .scroll-service-icon {
    width: 55px;
    height: 55px;
    font-size: 11px;
  }

  .scroll-service-label {
    font-size: 10px;
    max-width: 70px;
  }
}

.accounting-section {
  margin-bottom: 24px;
}

.accounting-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.accounting-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.accounting-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.record-date {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.record-amount {
  font-size: 14px;
  font-weight: bold;
}

.record-amount.debit {
  color: #e74c3c; /* Red for debit */
}

.record-amount.credit {
  color: #27ae60; /* Green for credit */
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.record-detail {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.detail-label {
  font-size: 11px;
  color: #888;
  font-weight: 500;
  min-width: 60px;
}

.detail-value {
  font-size: 12px;
  color: #333;
  text-align: left;
  flex: 1;
}

.type-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  text-align: center;
}

.debit-badge {
  background-color: #ffeaea;
  color: #e74c3c;
}

.credit-badge {
  background-color: #e8f7ef;
  color: #27ae60;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #4a6baf;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #888;
  background: white;
  border-radius: 12px;
  border: 1px dashed #ddd;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .accounting-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .accounting-card {
    padding: 12px;
  }
  
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .record-amount {
    align-self: flex-end;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .accounting-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1025px) {
  .accounting-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* Existing styles remain below */
.bank-app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f5f5f5;
  direction: rtl;
}

.main-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  padding-bottom: 80px; /* Space for bottom navigation */
}

.account-summary {
  background: linear-gradient(135deg, #4a6baf, #2c4a8c);
  color: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.account-type {
  font-size: 16px;
  margin-bottom: 8px;
  opacity: 0.9;
}

.account-balance {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.balance-label {
  font-size: 14px;
  opacity: 0.8;
}

.balance-amount {
  font-size: 18px;
  font-weight: bold;
}
/* جزییات Button Styles */
.accounting-card .card-body button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-top: 8px;
}

.accounting-card .card-body button:hover {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.accounting-card .card-body button:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.accounting-card .card-body button:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.3);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.request-modal {
  width: 90%;
  max-width: 480px;
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.25);
  direction: rtl;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
}

.form-row {
  margin: 12px 0;
  display: flex;
  flex-direction: column;
}

.form-row label {
  margin-bottom: 4px;
  font-weight: bold;
}

.form-row input,
.form-row select {
  padding: 10px;
  border: 1px solid #bbb;
  border-radius: 8px;
  font-size: 14px;
}

.modal-footer {
  text-align: center;
  margin-top: 15px;
}

.submit-btn {
  background: #2b57ff;
  color: white;
  padding: 10px 22px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 15px;
}
.currency-prices-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 15px;
  padding: 15px;
  margin: 15px 0;
  color: white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  max-height: 320px; /* Collapsed height */
  overflow: hidden;
  position: relative;
}

.currency-prices-section.expanded {
  max-height: 600px; /* Expanded height */
  overflow-y: auto;
}

.currency-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  position: sticky;
  top: 0;
  z-index: 10;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 5px 0;
}

.currency-header .section-title {
  font-size: 1.2rem;
  font-weight: bold;
  margin: 0;
  color: white;
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.refresh-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
  font-size: 1rem;
}

.expand-btn {
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
  font-size: 1.2rem;
  font-weight: bold;
}

.expand-btn:hover {
  background: rgba(255, 255, 255, 0.25);
}

.expand-btn.expanded {
  transform: rotate(180deg);
}

.refresh-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.refresh-btn.rotating .refresh-icon {
  animation: rotate 1s linear infinite;
}

.currency-content {
  height: calc(100% - 50px);
}

.currency-grid-container {
  height: 100%;
  overflow-y: hidden;
}

.currency-prices-section.expanded .currency-grid-container {
  overflow-y: visible;
}

.currency-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 10px;
  margin-bottom: 15px;
}

.currency-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 5px;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-height: 5px;
}

.currency-card:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
}

.currency-card.highlighted {
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.currency-flag {
  font-size: 1.5rem;
  margin-left: 10px;
  min-width: 30px;
  text-align: center;
}

.currency-info {
  flex: 1;
  min-width: 0;
}

.currency-code {
  font-size: 1rem;
  font-weight: bold;
  margin-bottom: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.currency-name {
  font-size: 0.75rem;
  opacity: 0.9;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.currency-price {
  text-align: left;
  min-width: 60px;
}

.price-value {
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  direction: ltr;
  text-align: right;
}

.price-time {
  font-size: 0.65rem;
  opacity: 0.8;
  direction: ltr;
  text-align: right;
}

/* Loading, Error, Empty states adjustments */
.currency-loading,
.currency-error,
.currency-empty {
  padding: 20px 0;
  min-height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
}

.loading-spinner {
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 2px solid white;
  width: 25px;
  height: 25px;
  animation: spin 1s linear infinite;
}

.currency-error {
  background: rgba(255, 0, 0, 0.1);
  border: 1px solid rgba(255, 0, 0, 0.3);
  border-radius: 8px;
  padding: 15px;
}

.error-icon {
  font-size: 1.5rem;
}

.retry-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 15px;
  border-radius: 15px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.3s ease;
}

.retry-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.currency-empty {
  opacity: 0.8;
}

.empty-icon {
  font-size: 2.5rem;
}

/* Scrollbar styling */
.currency-prices-section.expanded::-webkit-scrollbar {
  width: 6px;
}

.currency-prices-section.expanded::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
}

.currency-prices-section.expanded::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 10px;
}

.currency-prices-section.expanded::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.4);
}

/* Responsive Design */
@media (max-width: 768px) {
  .currency-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .currency-header .section-title {
    font-size: 1.1rem;
  }
  
  .currency-prices-section {
    max-height: 280px;
    margin: 10px 0;
    padding: 12px;
  }
  
  .currency-prices-section.expanded {
    max-height: 500px;
  }
}

@media (max-width: 480px) {
  .currency-grid {
    grid-template-columns: 1fr;
  }
  
  .currency-prices-section {
    border-radius: 12px;
    max-height: 260px;
  }
  
  .currency-prices-section.expanded {
    max-height: 450px;
  }
}

/* Desktop view */
@media (min-width: 769px) {
  .currency-prices-section {
    max-width: 100%;
    margin: 15px auto;
    padding: 18px;
    max-height: 300px;
  }
  
  .currency-prices-section.expanded {
    max-height: 550px;
  }
  
  .currency-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 15px;
  }
  
  .currency-header .section-title {
    font-size: 1.3rem;
  }
  
  .currency-code {
    font-size: 1.1rem;
  }
  
  .currency-name {
    font-size: 0.8rem;
  }
  
  .price-value {
    font-size: 1.1rem;
  }
}

/* Large desktop */
@media (min-width: 1200px) {
  .currency-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
}
.currency-prices-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 15px;
  padding: 15px;
  margin: 15px 0;
  color: white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  max-height: 320px; /* Collapsed height */
  overflow: hidden;
}

.currency-prices-section.expanded {
  max-height: 600px; /* Expanded height */
  overflow-y: auto; /* Make entire section scrollable */
}

/* Scrollbar styling for the entire section */
.currency-prices-section.expanded::-webkit-scrollbar {
  width: 6px;
}

.currency-prices-section.expanded::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  margin: 5px;
}

.currency-prices-section.expanded::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 10px;
}

.currency-prices-section.expanded::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.4);
}

/* For Firefox */
.currency-prices-section.expanded {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1);
}

.currency-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  position: sticky;
  top: 0;
  z-index: 10;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 5px 0;
}
/* BACK ARROW */
.back-arrow {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: var(--radius);
  transition: background-color .3s ease;
  color: var(--text);
}

.back-arrow:hover {
  background: var(--surface-hover);
}

/* BASE LAYOUT */
.bank-app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--bg);
  color: var(--text);
  direction: rtl;
}

.main-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  padding-bottom: 80px;
}

/* ACCOUNT SUMMARY */
.account-summary {
  background: var(--gradient-primary);
  color: var(--text-light);
  border-radius: var(--radius);
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: var(--shadow-2);
}

.account-type {
  font-size: 16px;
  margin-bottom: 8px;
  opacity: .9;
}

.account-balance {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.balance-label {
  font-size: 14px;
  opacity: .8;
}

.balance-amount {
  font-size: 18px;
  font-weight: bold;
  margin-left: auto;
}

/* SECTION TITLE */
.section-title {
  font-size: 16px;
  margin: 20px 0 12px;
  padding: 15px;
  color: var(--heading);
  font-weight: 600;
}

/* SERVICES GRID */
.services-grid {
  display: grid;
  grid-template-columns: repeat(4,1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.service-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.service-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--surface-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  font-size: 12px;
  color: var(--primary);
  font-weight: bold;
  box-shadow: var(--shadow-1);
}

.service-label {
  font-size: 12px;
  color: var(--subtext);
}

/* INFO CARD */
.additional-text {
  background: var(--card-bg);
  border-radius: var(--radius);
  padding: 16px;
  margin-bottom: 20px;
  text-align: center;
  box-shadow: var(--shadow-1);
  color: var(--subtext);
}

.account-number {
  margin-top: 8px;
  font-weight: bold;
  color: var(--primary);
}

/* HORIZONTAL SERVICES */
.horizontal-services-section {
  margin-bottom: 24px;
}

.horizontal-scroll-container {
  overflow-x: auto;
  margin: 0 -16px;
  padding: 8px 16px;
}

.horizontal-scroll-container::-webkit-scrollbar {
  display: none;
}

.scroll-content {
  display: flex;
  gap: 16px;
  min-width: min-content;
}

.scroll-service-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 80px;
  flex-shrink: 0;
  cursor: pointer;
  border-radius: var(--radius);
  padding: 8px;
  transition: transform .25s ease, background .25s ease;
}

.scroll-service-item:hover {
  background: var(--surface-hover);
  transform: translateY(-2px) scale(1.05);
}

.scroll-service-icon {
  width: 60px;
  height: 60px;
  border-radius: var(--radius);
  background: var(--card-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  box-shadow: var(--shadow-1);
  border: 1px solid var(--border);
  overflow: hidden;
}

.scroll-service-icon img {
  max-width: 70%;
  max-height: 70%;
  object-fit: contain;
}

.scroll-service-label {
  font-size: 11px;
  color: var(--subtext);
  line-height: 1.3;
  max-width: 80px;
}

/* ACCOUNTING SECTION */
.accounting-section {
  margin-bottom: 24px;
}

.accounting-grid {
  display: grid;
  gap: 16px;
  margin-bottom: 20px;
}

/* RESPONSIVE GRID */
@media (min-width:1200px){
  .accounting-grid{grid-template-columns:repeat(3,1fr);}
}

@media (min-width:769px) and (max-width:1199px){
  .accounting-grid{grid-template-columns:repeat(2,1fr);}
}

@media (max-width:768px){
  .accounting-grid{grid-template-columns:1fr;}
}

/* ACCOUNTING CARD */
.accounting-card {
  background: var(--card-bg);
  border-radius: var(--radius);
  padding: 16px;
  box-shadow: var(--shadow-1);
  border: 1px solid var(--border);
  transition: transform .2s ease, box-shadow .2s ease;
}

.accounting-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-2);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border);
}

.record-date {
  font-size: 12px;
  color: var(--subtext);
  font-weight: 500;
}

.record-amount {
  font-size: 14px;
  font-weight: bold;
}

.record-amount.debit {
  color: var(--red);
}

.record-amount.credit {
  color: var(--green);
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.record-detail {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.detail-label {
  font-size: 11px;
  color: var(--subtext);
  font-weight: 500;
  min-width: 60px;
}

.detail-value {
  font-size: 12px;
  color: var(--text);
  text-align: left;
  flex: 1;
}

/* BADGES */
.type-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  text-align: center;
}

.debit-badge {
  background: var(--error-bg);
  color: var(--red);
  border: 1px solid var(--error-border);
}

.credit-badge {
  background: var(--success-bg);
  color: var(--green);
  border: 1px solid var(--success-border);
}

/* LOADING STATE */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: var(--subtext);
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border);
  border-top: 3px solid var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  from {transform:rotate(0deg);}
  to {transform:rotate(360deg);}
}

/* EMPTY STATE */
.empty-state {
  text-align: center;
  padding: 40px;
  color: var(--subtext);
  background: var(--card-bg);
  border-radius: var(--radius);
  border: 1px dashed var(--border);
}

/* MOBILE */
@media (max-width:480px){

  .services-grid{
    grid-template-columns:repeat(4,1fr);
    gap:12px;
  }

  .service-icon{
    width:50px;
    height:50px;
    font-size:10px;
  }

  .service-label{
    font-size:10px;
  }

  .scroll-service-icon{
    width:55px;
    height:55px;
  }

  .scroll-service-label{
    font-size:10px;
    max-width:70px;
  }

}

.daily-report-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 15px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.report-table-container {
  width: 100%;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  text-align: right;
}

.report-table th {
  font-size: 32px;
  color: #333;
}

.report-table th,
.report-table td {
  padding: 10px;
  color: white;
  border-bottom: 1px solid rgba(255,255,255,0.2);
}

.report-table th {
  font-size: 1.2em;
}

/* small tablet / phone improvements */
@media (max-width: 768px) {
  .daily-report-section {
    padding: 12px;
  }

  .currency-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .currency-header h3 {
    font-size: 1rem;
    margin: 0 !important;
    padding-right: 5px;
    line-height: 1.4;
  }

  .report-table th,
  .report-table td {
    font-size: 0.92rem;
    padding: 8px;
  }
}

/* stacked card layout for phones */
@media (max-width: 576px) {
  .report-table {
    border-collapse: separate;
    border-spacing: 0 10px;
    margin-top: 12px;
  }

  .report-table thead {
    display: none;
  }

  .report-table,
  .report-table tbody,
  .report-table tr,
  .report-table td {
    display: block;
    width: 100%;
  }

  .report-table tr {
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 12px;
    padding: 10px 12px;
    margin-bottom: 10px;
    backdrop-filter: blur(2px);
  }

  .report-table td {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid rgba(255,255,255,0.12);
    text-align: right;
    white-space: normal;
    word-break: break-word;
  }

  .report-table td:last-child {
    border-bottom: none;
  }

  .report-table td::before {
    content: attr(data-label);
    font-weight: 700;
    color: rgba(255,255,255,0.9);
    flex: 0 0 40%;
    text-align: right;
  }

  .report-table td > * ,
  .report-table td {
    color: white;
  }
}
.today-taraz-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 15px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.today-taraz-filter {
  margin-top: 12px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  flex-wrap: wrap;
}

.cm-label {
  color: white;
  font-weight: 600;
  font-size: 0.95rem;
}

.cm-select {
  min-width: 180px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.25);
  background: rgba(255,255,255,0.14);
  color: white;
  outline: none;
  font-size: 0.95rem;
  backdrop-filter: blur(2px);
}

.cm-select option {
  color: #222;
  background: white;
}

.cm-select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.today-taraz-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 15px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}
/* استایل مبالغ: همیشه یک خط */
.price-container {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap; /* جلوگیری از شکستن خط */
  overflow: hidden;
}

.price-item {
  font-size: 0.9em;
}

/* مدیریت عنوان در دسکتاپ */
.desktop-text {
  display: inline;
}

/* استایل موبایل */
@media (max-width: 576px) {
  /* مخفی کردن متن عنوان در موبایل (چون می‌خواهیم از data-label استفاده کند) */
  .desktop-text {
    display: none;
  }

  /* نمایش عنوان از طریق data-label */
  .title-cell::before {
    content: attr(data-label);
    font-weight: bold;
    color: #fff;
  }
  
  /* مرتب‌سازی مبالغ در موبایل */
  .price-container {
    flex-direction: column; /* اگر در موبایل جا نبود، زیر هم می‌آید */
    align-items: flex-end;
    gap: 2px;
  }
  
  .price-item {
    font-size: 1.2em;
  }
}

.amount-cell {
  white-space: nowrap !important;
}

.header-actions{
  display: flex;
  align-items: center;
  gap: 10px;
}

.toggle-btn{
  display: inline-flex;
  align-items: center;
  gap: 6px;

  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.25);
  background: rgba(255,255,255,0.12);
  color: #fff;
  cursor: pointer;
  backdrop-filter: blur(2px);
}

.toggle-btn:disabled{
  opacity: .6;
  cursor: not-allowed;
}

.toggle-icon{
  display: inline-block;
  transition: transform .2s ease;
  font-weight: 900;
  line-height: 1;
}

/* وقتی collapsed است ^ را برعکس کنید (شبیه باز/بسته) */
.toggle-icon.collapsed{
  transform: rotate(180deg);
}

.toggle-text{
  font-size: 0.9rem;
  font-weight: 600;
}

/* موبایل: فقط آیکن بماند (اختیاری) */
@media (max-width: 576px){
  .toggle-text{
    display: none;
  }
}
.actions-group {
  display: flex;
  align-items: center;
  gap: 4px; 
}

.toggle-btn, .refresh-btn {
  margin: 0; 
  height: 35px; 
}
.daily-sale-section{
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 15px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.daily-sale-content{
  margin-top: 10px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

/* هر چارت */
.chart-box{
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.18);
  border-radius: 12px;
  padding: 12px;
  color: white;
}

.chart-header{
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
  color: white;
}

.chart-title{
  font-weight: 700;
}

.chart-type-select{
  min-width: 110px;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.25);
  background: rgba(255,255,255,0.14);
  color: white;
  outline: none;
}

.chart-type-select option{
  color: #222;
  background: white;
}

/* ارتفاع لازم برای chart.js چون maintainAspectRatio: false دارید */
.chart-container{
  position: relative;
  min-height: 260px;
}

/* موبایل */
@media (max-width: 576px){
  .daily-sale-content{
    grid-template-columns: 1fr;
  }
  .chart-container{
    min-height: 240px;
  }
}
td {
  display: block;
  width: 100%;
}
.title-cell::before {
  content: attr(data-label);
  display: inline-block;
  min-width: 100%;  /* Forces full width of parent */
  word-break: keep-all;  /* Prevents early breaks */
}
/* Layout to stack Title and Button vertically */
.title-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

/* Detail Button Styling */
.detail-link-btn {
  margin-left: auto;
  margin-top: 10px;
  gap: 6px;
  padding: 4px 10px;
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-size: 0.75rem;
  transition: all 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin-right: 5px;
}

.detail-link-btn:hover {
  background-color: var(--primary);
  border-color: var(--primary);
  transform: translateY(-1px);
}

.detail-link-btn .icon {
  font-size: 0.85rem;
}

/* Dark mode adjustments if needed */
html[data-theme="dark"] .detail-link-btn {
  background-color: var(--card-border);
}
/* Modal Base */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--modal-overlay);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  backdrop-filter: blur(8px);
  padding: 20px;
}

.detail-modal {
  background: var(--card-bg);
  width: 100%;
  max-width: 900px;
  max-height: 85vh;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 50px var(--shadow-2);
  border: 1px solid var(--card-border);
  color: var(--text);
  overflow: hidden;
}

/* Modal Header */
.modal-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--card-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--card-bg);
}

.modal-header h3 {
  margin: 0;
  color: var(--text);
  font-size: 1.25rem;
}

.close-btn {
  background: var(--bg);
  border: 1px solid var(--card-border);
  color: var(--text);
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.5rem;
  transition: all 0.2s;
}

.close-btn:hover {
  background: var(--primary);
  color: white;
}

/* Table in Modal */
.modal-body {
  padding: 0; /* Let the container handle padding */
  overflow-y: auto;
  background: var(--card-bg);
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
  color: var(--text);
}

.detail-table th {
  background: var(--bg);
  color: var(--muted);
  padding: 12px 16px;
  text-align: right;
  font-weight: 600;
  position: sticky;
  top: 0;
  border-bottom: 2px solid var(--card-border);
  z-index: 10;
}

.detail-table td {
  padding: 14px 16px;
  border-bottom: 1px solid var(--card-border);
  font-size: 0.95rem;
}

.detail-table tr:hover {
  background: var(--hover);
}

/* Alignment and Special Columns */
.ltr-text {
  direction: ltr;
  text-align: left;
  font-family: 'Tahoma', sans-serif; /* For better number reading */
}

.no-data {
  padding: 40px;
  text-align: center;
  color: var(--muted);
}

/* Button in main dashboard table */
.detail-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--bg);
  border: 1px solid var(--card-border);
  color: var(--primary);
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s;
  white-space: nowrap;
}

.detail-link-btn:hover {
  background: var(--primary);
  color: white;
  transform: translateY(-1px);
}

.detail-link-btn .icon {
  font-size: 1rem;
}
/* Detail Modal Table Styles */
.detail-table {
  width: 100%;
  border-collapse: collapse;
  color: var(--text);
  background: var(--card-bg);
}

.detail-table thead th {
  background: var(--bg);
  color: var(--text);
  padding: 12px 16px;
  text-align: right;
  font-weight: 600;
  font-size: 0.95rem;
  border-bottom: 2px solid var(--card-border);
  position: sticky;
  top: 0;
  z-index: 10;
}

.detail-table tbody td {
  padding: 12px 16px;
  border-bottom: 1px solid var(--card-border);
  font-size: 0.9rem;
  color: var(--text);
}

.detail-table tbody tr:hover {
  background: var(--hover);
  transition: background 0.2s ease;
}

.detail-table .amount-cell {
  font-weight: 500;
  direction: rtl;
  text-align: right;
  font-family: monospace;
  font-size: 0.95rem;
}

.detail-table .currency-cell {
  font-weight: 500;
  color: var(--primary);
}

/* Mobile responsive for detail table */
@media (max-width: 768px) {
  .detail-table thead {
    display: none;
  }
  
  .detail-table tbody tr {
    display: block;
    margin-bottom: 16px;
    border: 1px solid var(--card-border);
    border-radius: 12px;
    background: var(--card-bg);
  }
  
  .detail-table tbody td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 16px;
    border-bottom: 1px solid var(--card-border);
    gap: 12px;
  }
  
  .detail-table tbody td:last-child {
    border-bottom: none;
  }
  
  .detail-table tbody td::before {
    content: attr(data-label);
    font-weight: 600;
    color: var(--text);
    flex: 0 0 40%;
    font-size: 0.85rem;
  }
  
  .detail-table tbody td .amount-cell,
  .detail-table tbody td .currency-cell {
    flex: 1;
  }
}

/* Modal body scrolling */
.modal-body {
  overflow-y: auto;
  max-height: calc(85vh - 70px);
  padding: 0;
}

/* No data state */
.modal-body .no-data {
  padding: 40px;
  text-align: center;
  color: var(--subtext);
  background: var(--card-bg);
}

.detail-item{
  color: var(--text);
}

@media (max-width: 768px) {
  .detail-table tbody td::before {
    content: attr(data-label);
    color: var(--muted);
    font-weight: 600;
    font-size: 0.85rem;
    flex: 0 0 40%;
    text-align: right;
  }

  .detail-table tbody td .detail-item {
    color: var(--text);
  }
}
/* Monthly Activity Section */
.monthly-activity-section {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px;
    padding: 15px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.monthly-selector {
    margin-top: 10px;
    margin-bottom: 10px;
}

.act-select {
    width: 100%;
    max-width: 400px;
    padding: 10px 14px;
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.25);
    background: rgba(255,255,255,0.14);
    color: white;
    outline: none;
    font-size: 0.95rem;
    backdrop-filter: blur(2px);
}

.act-select option {
    color: #222;
    background: white;
}

.act-select:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.monthly-content {
    margin-top: 10px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.monthly-table-container {
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.18);
    border-radius: 12px;
    padding: 12px;
    color: white;
    overflow-x: auto;
}

.monthly-table {
    width: 100%;
    border-collapse: collapse;
    direction: rtl;
}

.monthly-table thead th {
    padding: 8px 12px;
    text-align: right;
    font-weight: 600;
    border-bottom: 1px solid rgba(255,255,255,0.2);
}

.monthly-table tbody td {
    padding: 8px 12px;
    border-bottom: 1px solid rgba(255,255,255,0.1);
}

.monthly-table tbody tr:last-child td {
    border-bottom: none;
}

.monthly-table .amount-cell {
    direction: ltr;
    text-align: right;
    font-variant-numeric: tabular-nums;
}

@media (max-width: 768px) {
    .monthly-content {
        grid-template-columns: 1fr;
    }
    
    .act-select {
        max-width: 100%;
    }
}

/* Monthly Activity Section */
.monthly-activity-section {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px;
    padding: 15px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.monthly-selector {
    margin-top: 10px;
    margin-bottom: 10px;
}

.act-select {
    width: 100%;
    max-width: 400px;
    padding: 10px 14px;
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.25);
    background: rgba(255,255,255,0.14);
    color: white;
    outline: none;
    font-size: 0.95rem;
    backdrop-filter: blur(2px);
}

.act-select option {
    color: #222;
    background: white;
}

.act-select:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Monthly Placeholder */
.monthly-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 30px 20px;
    color: rgba(255,255,255,0.8);
    font-size: 16px;
    text-align: center;
}

.placeholder-icon {
    font-size: 32px;
}

.monthly-content {
    margin-top: 10px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.monthly-table-container {
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.18);
    border-radius: 12px;
    padding: 12px;
    color: white;
    overflow-x: auto;
}

.monthly-table {
    width: 100%;
    border-collapse: collapse;
    direction: rtl;
}

.monthly-table thead th {
    padding: 8px 12px;
    text-align: right;
    font-weight: 600;
    border-bottom: 1px solid rgba(255,255,255,0.2);
}

.monthly-table tbody td {
    padding: 8px 12px;
    border-bottom: 1px solid rgba(255,255,255,0.1);
}

.monthly-table tbody tr:last-child td {
    border-bottom: none;
}

.monthly-table .amount-cell {
    direction: ltr;
    text-align: right;
    font-variant-numeric: tabular-nums;
}

@media (max-width: 768px) {
    .monthly-content {
        grid-template-columns: 1fr;
    }
    
    .act-select {
        max-width: 100%;
    }
}
</style>